import React, {
  useCallback,
  useEffect,
  useState,
  useRef,
  useContext,
} from 'react'

import TripNameField from './TripDetailsFields/TripNameField'

import MessageBanner from 'components/Luxkit/Banners/MessageBanner'
import ModalBody from 'components/Luxkit/Modal/ModalBody'
import ModalContent from 'components/Luxkit/Modal/ModalContent'
import ModalFooter from 'components/Luxkit/Modal/ModalFooter'
import ModalHeader from 'components/Luxkit/Modal/ModalHeader'
import { ModalElementContext } from 'contexts/ModalProvider'
import { UpdateTripError } from 'tripPlanner/api/types'
import { useUpdateTrip } from 'tripPlanner/hooks/api'
import useModalCallbacks from 'tripPlanner/hooks/View/useModalCallbacks'
import { BasicTrip, FullTrip } from 'tripPlanner/types/common'

interface Props {
  trip: BasicTrip | FullTrip
}

function EditTripName(props: Props) {
  const { trip } = props
  const inputRef = useRef<HTMLInputElement>(null)

  const { closeModal, goBack } = useModalCallbacks()
  const modalElement = useContext(ModalElementContext)

  const onCloseModalClicked = useCallback(() => {
    closeModal()
    modalElement?.resolve()
  }, [closeModal, modalElement])
  const [name, setName] = useState(trip.name)
  const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined)

  const { mutate, isLoading } = useUpdateTrip({
    onSuccess: () => {
      onCloseModalClicked()
    },
    onError: (e: UpdateTripError) => {
      if (e.status === 422) {
        setErrorMsg(e.message)
      }
    },
  })

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.select()
    }
  }, [])

  const onUpdate = useCallback(() => {
    if (name.length) {
      mutate({
        tripId: trip.id,
        name,
      })
    }
  }, [mutate, name, trip.id])

  return (
    <>
      <ModalHeader
        title="Rename your trip plan"
        onBackButtonClick={goBack}
        onCloseButtonClick={onCloseModalClicked}
      />
      <ModalBody>
        <ModalContent>
          <TripNameField
            value={name}
            onChange={setName}
            autoSelect
            disabled={isLoading}
            data-testid="trip-popup-trip-input"
          />
          {errorMsg && <MessageBanner kind="critical" description={errorMsg} />}
        </ModalContent>
      </ModalBody>
      <ModalFooter
        primaryActionProps={{
          children: 'Save',
          disabled: isLoading || name.length == 0,
          onClick: onUpdate,
        }}
        secondaryActionProps={{
          children: 'Cancel',
          disabled: isLoading,
          onClick: onCloseModalClicked,
        }}
      />
    </>
  )
}

export default EditTripName
